import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ShoppingCart, UtensilsCrossed, Receipt, LogOut, Ship as ShipIcon, BedDouble, Anchor, Users as UsersIcon, Download, Sparkles, PlayCircle } from "lucide-react";
import { useAuth } from "../lib/api";
import CountdownClock from "./CountdownClock";
import NotificationsBell from "./NotificationsBell";
import TourModal from "./TourModal";
import NotesChatFab from "./NotesChatFab";

const BASE_NAV = [
  { to: "/", label: "Panel", icon: Home, testid: "nav-dashboard", end: true },
  { to: "/gemi", label: "Tekne", icon: Anchor, testid: "nav-ship" },
  { to: "/alisveris", label: "Menü & Erzak", icon: ShoppingCart, testid: "nav-menu-erzak" },
  { to: "/muhasebe", label: "Muhasebe", icon: Receipt, testid: "nav-actuals" },
  { to: "/kabinler", label: "Kabinler", icon: BedDouble, testid: "nav-cabins" },
  { to: "/anilar", label: "Anılar", icon: Sparkles, testid: "nav-anilar" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const navItems = BASE_NAV;
  const [tourOpen, setTourOpen] = React.useState(false);
  const handleLogout = () => {
    logout();
    navigate("/login");
  };
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(var(--background))" }}>
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-lg bg-white/80 border-b" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="max-w-5xl mx-auto px-3 pr-4 md:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
              <ShipIcon className="w-5 h-5" style={{ color: "hsl(var(--primary))" }} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-display font-semibold text-sm md:text-base whitespace-nowrap">Mavi Yolculuk &apos;26</div>
              <div className="flex items-center gap-1.5 text-[11px] whitespace-nowrap" style={{ color: "hsl(var(--text-2))" }}>
                <span className="hidden sm:inline">Merhaba, {user?.display_name}</span>
                <button
                  type="button"
                  onClick={() => setTourOpen(true)}
                  data-testid="tour-play-btn"
                  title="Uygulama Turu"
                  aria-label="Uygulama Turu"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-md transition hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--primary))]"
                >
                  <PlayCircle className="w-3.5 h-3.5" style={{ color: "hsl(var(--primary))" }} />
                </button>
                {isAdmin && (
                  <NavLink
                    to="/kullanicilar"
                    data-testid="users-icon-link"
                    title="Kullanıcılar"
                    aria-label="Kullanıcılar"
                    className={({ isActive }) =>
                      `inline-flex items-center justify-center w-5 h-5 rounded-md transition ${
                        isActive ? "text-[hsl(var(--primary))] bg-[hsl(var(--surface-2))]" : "hover:text-[hsl(var(--text-1))] hover:bg-[hsl(var(--surface-2))]"
                      }`
                    }
                  >
                    <UsersIcon className="w-3.5 h-3.5" />
                  </NavLink>
                )}
                <NavLink
                  to="/yardim"
                  data-testid="help-icon-link"
                  title="Yardım · Uygulamayı kur"
                  aria-label="Yardım"
                  className={({ isActive }) =>
                    `inline-flex items-center justify-center w-5 h-5 rounded-md transition ${
                      isActive ? "text-[hsl(var(--primary))] bg-[hsl(var(--surface-2))]" : "hover:text-[hsl(var(--text-1))] hover:bg-[hsl(var(--surface-2))]"
                    }`
                  }
                >
                  <Download className="w-3.5 h-3.5" />
                </NavLink>
                <NotificationsBell />
              </div>
            </div>
          </div>
          {/* countdown — always shown, between brand and nav */}
          <div className="flex justify-center px-1 md:px-2 flex-shrink min-w-0">
            <CountdownClock />
          </div>
          {/* desktop nav — kaydırılabilir, sağ kenarda fade indicator */}
          <div className="hidden md:block flex-1 min-w-0 relative">
            <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar pr-8">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid + "-desktop"}
                  className={({ isActive }) =>
                    `px-2.5 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap flex-shrink-0 ${
                      isActive ? "bg-[hsl(var(--surface-2))] text-[hsl(var(--text-1))]" : "text-[hsl(var(--text-2))] hover:bg-[hsl(var(--surface-2))]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <button data-testid="logout-btn-desktop" onClick={handleLogout} className="ml-2 tk-btn-ghost !py-2 !px-3 text-sm flex items-center gap-1.5 flex-shrink-0">
                <LogOut className="w-4 h-4" /> Çıkış
              </button>
            </nav>
            {/* Fade indicator — kaydırılabilir olduğuna ipucu */}
            <div className="absolute right-0 top-0 bottom-0 w-6 pointer-events-none" style={{ background: "linear-gradient(to right, transparent, hsl(var(--surface)))" }} />
          </div>
          {/* mobile logout */}
          <button data-testid="logout-btn-mobile" onClick={handleLogout} className="md:hidden p-2 rounded-lg flex-shrink-0 mr-0" style={{ color: "hsl(var(--text-2))" }} aria-label="Çıkış">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 md:px-6 py-5 pb-28 md:pb-8">
        <Outlet />
      </main>

      {/* mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-lg bg-white/90 border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className={`max-w-md mx-auto grid ${navItems.length === 8 ? "grid-cols-8" : navItems.length === 7 ? "grid-cols-7" : navItems.length === 6 ? "grid-cols-6" : "grid-cols-5"}`}>
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid + "-mobile"}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] ${
                  isActive ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--text-2))]"
                }`
              }
            >
              <n.icon className="w-5 h-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
      {tourOpen && <TourModal onClose={() => setTourOpen(false)} />}
      <NotesChatFab />
    </div>
  );
}
